import { EyeIcon, PenIcon } from "@xola/icons";
import { Badge, flash, Logo, Popover, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { assign } from "lodash";
import pluralize from "pluralize";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { DigitalWaiver, Experience } from "types";
import { api } from "../../../api/api";
import { populateResource } from "../../../hooks/helper";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { getDefaultPhotoUrl } from "../../Announcements/AnnouncementsDrawer";
import { NoTranslate } from "../../Common/NoTranslate";
import { ProductImage } from "../../Common/ProductImage";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { DigitalWaiversExperiencesSelectorModal } from "./DigitalWaiversExperiencesSelectorModal";

interface DigitalWaiversItemProps {
    waiverTemplate: DigitalWaiver;
    waiverTemplates: DigitalWaiver[];
    experiences: Experience[];
    isActive?: boolean;
    className?: string;
    onPreview: (template: DigitalWaiver) => void;
}

export const DigitalWaiversItem = ({
    waiverTemplate,
    waiverTemplates,
    experiences,
    isActive = false,
    className,
    onPreview,
}: DigitalWaiversItemProps) => {
    const invalidate = useInvalidate();
    const [isOpen, setIsOpen] = useState(false);

    const { mutate: unlinkWaiver } = useMutation(
        async (experience: Experience) => {
            try {
                return await api.delete(`/experiences/${experience.id}/waiver`);
            } catch {
                flash.danger("Error un-linking the waiver to this experience");
            }
        },
        {
            onMutate: async (updatedExperience: Experience) => {
                delete updatedExperience.waiverPreference;
                populateResource("experiences", updatedExperience);
                populateResource("products", updatedExperience);
                invalidate("waiverTemplates");
            },
        },
    );

    const { mutate: addWaiver } = useMutation(
        async (experience: Experience) => {
            try {
                return await api.post(`/experiences/${experience.id}/waiver`, {
                    parameterizedUrl: waiverTemplate.parameterizedUrl,
                    remoteId: waiverTemplate.id,
                    remoteSystem: waiverTemplate.remoteSystem,
                    url: waiverTemplate.url,
                });
            } catch {
                flash.danger("Error linking waiver to this experience");
            }
        },
        {
            onMutate: async (updatedExperience: Experience) => {
                const newExperience = assign(updatedExperience, {
                    waiverPreference: {
                        parameterizedUrl: waiverTemplate.parameterizedUrl,
                        remoteId: waiverTemplate.id,
                        remoteSystem: waiverTemplate.remoteSystem,
                        url: waiverTemplate.url,
                    },
                });
                populateResource("experiences", newExperience);
                populateResource("products", newExperience);
                invalidate("waiverTemplates");
            },
        },
    );

    const getSelection = () => {
        return experiences
            .filter((exp: Experience) => exp?.waiverPreference?.remoteId === waiverTemplate.id)
            .map((exp) => exp?.id);
    };

    const handleApply = ({ selected, all }: { selected: string[]; all: boolean }) => {
        for (const exp of experiences) {
            if (all) {
                selected = experiences.map((experience) => experience.id);
            }

            const hasExperience = selected.includes(exp.id);

            const waiverPreference = exp?.waiverPreference;
            const addWaiverPreference =
                hasExperience && (!waiverPreference || waiverPreference?.remoteId !== waiverTemplate.id);

            const deleteWaiverPreference =
                !hasExperience && waiverPreference && waiverPreference?.remoteId === waiverTemplate.id;

            if (addWaiverPreference) {
                addWaiver(exp);
            }

            if (deleteWaiverPreference) {
                unlinkWaiver(exp);
            }
        }
    };

    return (
        <div
            className={clsx(
                "ui-waiver-item flex cursor-pointer items-center justify-between overflow-hidden border-b border-b-gray-light px-5 py-4.5 hover:bg-gray-lighter",
                isActive && "bg-blue-lighter",
                className,
            )}
        >
            <div className="w-3/5 flex-shrink-0 space-y-1">
                <div className="flex items-center space-x-2">
                    <Logo src={waiverTemplate?.logo ?? getDefaultPhotoUrl()} className="mr-3 h-12 w-12 rounded-full" />
                    <NoTranslate as="p" className="truncate text-lg font-bold leading-p2 tracking-tightest">
                        <Tooltip key={waiverTemplate.id} content={<NoTranslate>{waiverTemplate.name}</NoTranslate>}>
                            {waiverTemplate.name}
                        </Tooltip>
                    </NoTranslate>
                </div>
            </div>
            <div className="mr-2 flex flex-auto justify-end space-x-4 text-lg font-bold">
                <BadgeRenderer key={waiverTemplate.id} waiverTemplate={waiverTemplate} experiences={experiences} />
                <div className="space-x-2">
                    <SecondaryButton size="small" icon={<PenIcon />} onClick={() => setIsOpen(true)}>
                        Manage Products
                    </SecondaryButton>
                    <SecondaryButton size="small" icon={<EyeIcon />} onClick={() => onPreview(waiverTemplate)}>
                        Preview
                    </SecondaryButton>
                </div>
            </div>
            <DigitalWaiversExperiencesSelectorModal
                isOpen={isOpen}
                value={{
                    selected: getSelection(),
                    all: getSelection().length === experiences.length,
                }}
                waiverTemplate={waiverTemplate}
                waiverTemplates={waiverTemplates}
                experiences={experiences}
                description="Manage which products this waiver is assigned to."
                onClose={() => setIsOpen(false)}
                onConfirm={handleApply}
            />
        </div>
    );
};

interface BadgeRendererProps {
    waiverTemplate: DigitalWaiver;
    experiences: Experience[];
}

const BadgeRenderer = ({ waiverTemplate, experiences }: BadgeRendererProps) => {
    const selectedExperiences = experiences.filter(
        (exp: Experience) => exp.waiverPreference?.remoteId === waiverTemplate.id,
    );

    return (
        <div>
            <Popover appendTo={document.body} placement="bottom">
                <Badge color="secondary" size="small">
                    <span>
                        <var data-var="count">{selectedExperiences.length}</var>&nbsp;
                        {pluralize("product", selectedExperiences.length)} selected
                    </span>
                </Badge>
                <Popover.Content>
                    <div className="max-h-100 overflow-y-auto">
                        {selectedExperiences.map((exp) => (
                            <span key={exp.id} className="flex items-center border-b border-b-gray-light p-4">
                                <ProductImage product={exp} size="tiny" />
                                <NoTranslate className="ml-2 font-bold text-black">{exp.name.trim()}</NoTranslate>
                            </span>
                        ))}
                    </div>
                </Popover.Content>
            </Popover>
        </div>
    );
};
